import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Save, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = {
  NOT_STARTED: { bg: '#64748b20', text: '#64748b' },
  ON_TRACK: { bg: '#10b98120', text: '#10b981' },
  AHEAD: { bg: '#06b6d420', text: '#06b6d4' },
  BEHIND: { bg: '#ef444420', text: '#ef4444' },
  COMPLETED: { bg: '#22c55e20', text: '#22c55e' },
  SUSPENDED: { bg: '#f59e0b20', text: '#f59e0b' },
};

export default function FCProgressEntry({ projectId }) {
  const [activities, setActivities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [periodType, setPeriodType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progressEntries, setProgressEntries] = useState({});
  const [discFilter, setDiscFilter] = useState('');
  const [disciplines, setDisciplines] = useState([]);
  const [purging, setPurging] = useState(false);
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [actsRes, periodsRes, discRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/fc/activities?limit=500${discFilter ? `&discipline=${discFilter}` : ''}`),
        api.get(`/v1/projects/${projectId}/fc/reporting-periods`),
        api.get(`/v1/projects/${projectId}/fc/disciplines`)
      ]);
      setActivities(actsRes.data.activities || []);
      const pData = periodsRes.data;
      const pList = pData?.periods || [];
      setPeriods(pList);
      setPeriodType(pData?.periodType || '');
      setDisciplines(discRes.data || []);

      // Auto-select today for daily, or current period for monthly
      if (!selectedDate && pList.length > 0) {
        const current = pList.find(p => p.isCurrent);
        if (current) setSelectedDate(current.periodId);
        else {
          // Pick the latest non-closed period
          const open = pList.filter(p => !p.isClosed);
          if (open.length) setSelectedDate(open[open.length - 1].periodId);
          else setSelectedDate(pList[pList.length - 1].periodId);
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, discFilter, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // For daily periods, allow date input instead of dropdown
  const isDaily = periodType === 'DAILY';

  // Initialize progress entries from existing actuals
  useEffect(() => {
    if (!selectedDate || activities.length === 0) return;
    const entries = {};
    activities.forEach(a => {
      const actual = (a.actualProgress || []).find(p => p.periodId === selectedDate);
      entries[a.id] = { mtdActualQty: actual?.mtdActualQty || 0, remarks: actual?.remarks || '' };
    });
    setProgressEntries(entries);
  }, [selectedDate, activities]);

  const updateEntry = (actId, field, value) => {
    setProgressEntries(prev => ({ ...prev, [actId]: { ...prev[actId], [field]: value } }));
  };

  const saveAll = async () => {
    setSaving(true);
    let saved = 0, errors = 0;
    for (const [actId, entry] of Object.entries(progressEntries)) {
      if (entry.mtdActualQty === undefined || entry.mtdActualQty === null) continue;
      try {
        await api.put(`/v1/projects/${projectId}/fc/activities/${actId}/progress/${selectedDate}`, {
          mtdActualQty: parseFloat(entry.mtdActualQty) || 0,
          remarks: entry.remarks || null
        });
        saved++;
      } catch { errors++; }
    }
    if (saved > 0) toast.success(`Saved progress for ${saved} activities`);
    if (errors > 0) toast.error(`${errors} entries failed`);
    fetchData();
    setSaving(false);
  };

  const periodInfo = periods.find(p => p.periodId === selectedDate);
  const isPeriodClosed = periodInfo?.isClosed;
  // A period is "future" when its start-date is strictly after today —
  // we cannot record actuals for work that hasn't happened yet.
  const isPeriodFuture = useMemo(() => {
    const start = String(periodInfo?.startDate || periodInfo?.periodId || '').slice(0, 10);
    return !!start && start > todayISO;
  }, [periodInfo, todayISO]);
  const inputsLocked = isPeriodClosed || isPeriodFuture;

  const purgeFuture = async () => {
    if (!window.confirm('Delete ALL actual entries dated after today across every activity?\nThis cannot be undone.')) return;
    setPurging(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/fc/actuals/purge-future`);
      toast.success(`Purged ${data.actualProgressPurged} activity actuals · ${data.ccManpowerDailyPurged} MP · ${data.ccEquipmentDailyPurged} EQ future rows`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed');
    } finally { setPurging(false); }
  };

  // Calculate summaries
  const totalPlanned = activities.reduce((sum, a) => {
    const d = (a.plannedDistribution || []).find(p => p.periodId === selectedDate);
    return sum + (d?.mtdPlanQty || 0);
  }, 0);
  const totalActual = Object.values(progressEntries).reduce((sum, e) => sum + (parseFloat(e.mtdActualQty) || 0), 0);

  // For daily: group periods by month for the date picker
  const monthGroups = useMemo(() => {
    if (!isDaily) return [];
    const groups = {};
    periods.forEach(p => {
      const month = p.periodId?.slice(0, 7);
      if (!groups[month]) groups[month] = { month, label: p.periodLabel?.slice(3) || month, days: [] };
      groups[month].days.push(p);
    });
    return Object.values(groups);
  }, [periods, isDaily]);

  // For the daily date input, map to valid period IDs
  const handleDateChange = (dateStr) => {
    // dateStr is YYYY-MM-DD from input[type=date]
    const matchingPeriod = periods.find(p => p.periodId === dateStr);
    if (matchingPeriod) {
      setSelectedDate(dateStr);
    } else {
      toast.error('Selected date is outside the reporting period range');
    }
  };

  return (
    <div className="space-y-4" data-testid="fc-progress-entry">
      {/* Period Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div>
            <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>
              {isDaily ? 'Select Date' : 'Reporting Period'}
            </label>
            {isDaily ? (
              <input type="date" value={selectedDate}
                onChange={e => handleDateChange(e.target.value)}
                min={periods[0]?.periodId}
                max={todayISO}
                className="px-3 py-2 rounded-lg text-sm border min-w-[180px]"
                style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="period-date-input"
                title="Actuals can only be recorded for dates up to today" />
            ) : (
              <select value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm border min-w-[180px]" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="period-select">
                {periods.map(p => {
                  const pStart = String(p.startDate || p.periodId).slice(0, 10);
                  const isFuture = pStart > todayISO;
                  return (
                    <option key={p.periodId} value={p.periodId} disabled={isFuture}>
                      {p.periodLabel} {p.isCurrent ? '(Current)' : ''} {p.isClosed ? '[CLOSED]' : ''}{isFuture ? ' [FUTURE — locked]' : ''}
                    </option>
                  );
                })}
              </select>
            )}
          </div>
          <select value={discFilter} onChange={e => setDiscFilter(e.target.value)}
            className="px-2 py-2 rounded-lg text-xs border mt-4" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
            <option value="">All Disciplines</option>
            {disciplines.map(d => <option key={d.disciplineCode} value={d.disciplineCode}>{d.disciplineCode}</option>)}
          </select>
          {isDaily && selectedDate && (
            <span className="text-xs mt-4 px-3 py-1.5 rounded-lg" style={{ background: 'var(--card-bg-alt)', color: 'var(--accent-primary)', border: '1px solid var(--border-color)' }}>
              {periodInfo?.periodLabel || selectedDate}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isPeriodClosed && (
            <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg" style={{ background: '#ef444420', color: '#ef4444' }}>
              <AlertTriangle className="w-3.5 h-3.5" /> Period closed
            </span>
          )}
          {isPeriodFuture && (
            <span className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg" style={{ background: '#f59e0b20', color: '#b45309' }}
                  data-testid="future-period-lock-badge">
              <AlertTriangle className="w-3.5 h-3.5" /> Future date · actuals locked
            </span>
          )}
          <Button size="sm" variant="outline" onClick={purgeFuture} disabled={purging}
                  data-testid="purge-future-actuals-btn"
                  title="Wipe every actual quantity / manpower / equipment entry with date > today">
            {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
            Purge Future Actuals
          </Button>
          <Button size="sm" onClick={saveAll} disabled={saving || inputsLocked} data-testid="save-progress-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save All Progress
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>Activities</p>
          <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{activities.length}</p>
        </div>
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>{isDaily ? 'Daily' : 'MTD'} Plan Total</p>
          <p className="text-lg font-bold" style={{ color: '#3b82f6' }}>{totalPlanned.toLocaleString(undefined, { maximumFractionDigits: 1 })}</p>
        </div>
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          <p className="text-[10px] font-medium" style={{ color: 'var(--text-muted)' }}>{isDaily ? 'Daily' : 'MTD'} Actual Total</p>
          <p className="text-lg font-bold" style={{ color: '#10b981' }}>{totalActual.toLocaleString(undefined, { maximumFractionDigits: 1 })}</p>
        </div>
      </div>

      {/* Progress Entry Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
          <table className="w-full text-xs" data-testid="progress-table">
            <thead>
              <tr style={{ background: 'var(--card-bg-alt)' }}>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Activity</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Disc</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Scope</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>UOM</th>
                <th className="p-2 text-right font-medium" style={{ color: '#3b82f6' }}>{isDaily ? 'Day' : 'MTD'} Plan</th>
                <th className="p-2 text-right font-medium" style={{ color: '#3b82f6' }}>Cum Plan</th>
                <th className="p-2 text-center font-medium" style={{ color: '#10b981' }}>{isDaily ? 'Day' : 'MTD'} Actual</th>
                <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Cum Actual</th>
                <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
                <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {activities.map(a => {
                const planEntry = (a.plannedDistribution || []).find(d => d.periodId === selectedDate);
                const existingActual = (a.actualProgress || []).find(p => p.periodId === selectedDate);
                const sc = STATUS_COLORS[a.status] || STATUS_COLORS.NOT_STARTED;
                const entry = progressEntries[a.id] || { mtdActualQty: 0, remarks: '' };

                return (
                  <tr key={a.id} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <td className="p-2">
                      <span className="font-mono text-[10px]" style={{ color: 'var(--accent-primary)' }}>{a.activityId}</span>
                      <p className="truncate max-w-[180px]" style={{ color: 'var(--text-primary)' }}>{a.activityName}</p>
                    </td>
                    <td className="p-2" style={{ color: 'var(--text-muted)' }}>{a.disciplineCode}</td>
                    <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{a.scopeQuantity?.toLocaleString()}</td>
                    <td className="p-2 text-right" style={{ color: 'var(--text-muted)' }}>{a.uom}</td>
                    <td className="p-2 text-right font-mono" style={{ color: '#3b82f6' }}>{planEntry?.mtdPlanQty?.toFixed(1) || '-'}</td>
                    <td className="p-2 text-right font-mono" style={{ color: '#3b82f6' }}>{planEntry?.ftmPlanQty?.toFixed(1) || '-'}</td>
                    <td className="p-2">
                      <input type="number" step="0.1" value={entry.mtdActualQty}
                        onChange={e => updateEntry(a.id, 'mtdActualQty', e.target.value)}
                        disabled={inputsLocked}
                        title={isPeriodFuture ? 'Future dates cannot be edited — actuals are only for work already performed' : ''}
                        className="w-20 px-2 py-1 rounded text-xs text-right border font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: '#10b981' }}
                        data-testid={`progress-input-${a.activityId}`} />
                    </td>
                    <td className="p-2 text-right font-mono" style={{ color: '#10b981' }}>
                      {existingActual?.ftmActualQty?.toFixed(1) || '-'}
                    </td>
                    <td className="p-2 text-center">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: sc.bg, color: sc.text }}>
                        {a.status}
                      </span>
                    </td>
                    <td className="p-2">
                      <input value={entry.remarks} onChange={e => updateEntry(a.id, 'remarks', e.target.value)}
                        disabled={inputsLocked} placeholder="..."
                        className="w-28 px-2 py-1 rounded text-xs border"
                        style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                    </td>
                  </tr>
                );
              })}
              {activities.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  No activities found. Create activities in the Activity Register tab first.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
